/**
 * Bookings API Client
 * Provides methods to interact with booking endpoints
 */

import { apiClient, ApiResponse } from './client';

export interface BookingBoatRef {
  id: string;
  name: string;
  type: string;
  location?: {
    name?: string;
  };
  images?: Array<{
    imageUrl: string;
    isPrimary?: boolean;
  }>;
}

export interface BookingCustomerRef {
  id: string;
  name: string;
  email: string;
  phone?: string;
  image?: string;
}

export interface Booking {
  id: string;
  bookingId: string;
  boatId: string | BookingBoatRef;
  customerId: string | BookingCustomerRef;
  startDate: string;
  endDate: string;
  startTime?: string;
  durationHours?: number;
  passengers: number;
  totalAmount: string;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED' | 'REJECTED';
  paymentStatus: 'PENDING' | 'PAID' | 'REFUNDED' | 'PARTIALLY_REFUNDED';
  specialRequests?: string;
  boat?: BookingBoatRef;
  customer?: BookingCustomerRef;
  payments?: Array<{
    id: string;
    amount: string;
    status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'REFUNDED' | 'PARTIALLY_REFUNDED';
  }>;
  _id?: string;
}

export interface BookingSearchParams {
  status?: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'rejected';
  boatId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

class BookingsApi {
  async getOperatorBookings(params?: BookingSearchParams): Promise<ApiResponse<Booking[]>> {
    const queryParams: Record<string, any> = {};

    if (params?.status) queryParams.status = params.status;
    if (params?.boatId) queryParams.boatId = params.boatId;
    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.endDate) queryParams.endDate = params.endDate;
    if (params?.page) queryParams.page = params.page;
    if (params?.limit) queryParams.limit = params.limit;

    return apiClient.get<Booking[]>('/bookings/operator/me', queryParams);
  }

  async getUserBookings(params?: BookingSearchParams): Promise<ApiResponse<Booking[]>> {
    const queryParams: Record<string, any> = {};

    if (params?.status) queryParams.status = params.status;
    if (params?.page) queryParams.page = params.page;
    if (params?.limit) queryParams.limit = params.limit;

    return apiClient.get<Booking[]>('/bookings/user/me', queryParams);
  }

  async getBookingById(id: string): Promise<ApiResponse<Booking>> {
    return apiClient.get<Booking>(`/bookings/${id}`);
  }

  async updateBookingStatus(
    id: string,
    status: 'confirmed' | 'rejected',
    reason?: string
  ): Promise<ApiResponse<Booking>> {
    return apiClient.patch<Booking>(`/bookings/${id}/status`, { status, reason });
  }

  async cancelBooking(id: string, reason?: string): Promise<ApiResponse<Booking>> {
    return apiClient.post<Booking>(`/bookings/${id}/cancel`, { reason });
  }

  async markPaymentCollected(id: string, reason?: string): Promise<ApiResponse<Booking>> {
    return apiClient.post<Booking>(`/bookings/${id}/mark-payment-collected`, { reason });
  }

  async createBooking(data: {
    boatId: string;
    startDate: string;
    endDate: string;
    startTime?: string;
    passengers: number;
    specialRequests?: string;
    insurance?: boolean;
    guests?: Array<{ name: string; age?: number; phone?: string; email?: string }>;
  }): Promise<ApiResponse<Booking>> {
    return apiClient.post<Booking>('/bookings', data);
  }
}

export const bookingsApi = new BookingsApi();
